import React, { useMemo, useRef } from "react";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { OpusAgentErrorBubble } from "./OpusAgentErrorBubble";
import { OPUS_AGENT_FOCUS_RING_CLASSNAME } from "./opusAgentCardStyles";
import { format } from "date-fns";
import {
  AGENT_BUBBLE_CLASS,
  BUBBLE_GROUP_CLASS,
  type FinishBeat,
  OpusAgentTurnMessage,
} from "./OpusAgentTurnMessage";
import { askOpusCapabilities } from "./agentCapabilities";
import { useStickToBottom } from "./useStickToBottom";
import { APPROVE_PLAN_MESSAGE, planProposalStates } from "./planProposals";
import { useOpusAgentCanvasStore } from "./useOpusAgentCanvasStore";
import { promptNeedsFile, questionsStates } from "./questionPrompts";
import type {
  OpusAgentTurnState,
  SendMessageOptions,
} from "./useOpusAgentChat";

interface OpusAgentThreadProps {
  turns: OpusAgentTurnState[];
  loading: boolean;
  loadFailed: boolean;
  // The drawer's scroll container (the thread is its content).
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  // Identity of the conversation being shown; a change re-pins to the bottom.
  chatKey: string | null;
  onSendMessage: (message: string, options?: SendMessageOptions) => void;
  onAttachFiles?: (message: string, send: boolean) => void;
  onResumePolling: (turnId: string) => void;
  onRetryLoad: () => void;
  onEditMessage?: (turnId: string, newMessage: string) => Promise<boolean>;
  // A bound agent's name: the opening message greets as that agent, with its
  // own suggested questions instead of the Ask Opus quick-starts, which
  // describe the unbound panel.
  agentName?: string | null;
  agentSuggestedQuestions?: readonly string[];
  // What the agent is for (its gallery template's description), used when it
  // has no capability bullets.
  agentDescription?: string | null;
  // The agent's "Here's what I can help with" bullets; none for an agent
  // made from scratch, which falls back to its description.
  agentCapabilities?: readonly string[];
  // Greets the reader by name in the opening message.
  userFirstName?: string | null;
  // Shown above the opening messages (the Ask Opus orb).
  openerIcon?: React.ReactNode;
  // The status row's avatar and each turn's finishing beat.
  finishBeat?: FinishBeat;
  // Whether this admin can build drafts, so the building quick-starts only
  // appear where Ask Opus can actually act on them.
  canBuild?: boolean;
  // Whether this chat can send (false for an archived or unavailable
  // agent): the latest plan then reads "Proposed" and offers no Approve.
  canSend?: boolean;
}

const COMPOSER_SELECTOR = '[data-testid="chat-composer-input"]';

// Focuses the composer of the panel that holds `from` (the nearest ancestor
// that contains one).
const focusNearestComposer = (from: HTMLElement | null) => {
  for (let node = from?.parentElement; node; node = node.parentElement) {
    const composer = node.querySelector<HTMLElement>(COMPOSER_SELECTOR);
    if (composer) {
      composer.focus();
      return;
    }
  }
};

// Quick-starts for the empty state: each fills and sends. Always shown on
// the unbound agent surface — the KB chat's generated SuggestedQuestions (and
// its flag) describe library lookups, not what the agent can do.
export const OPUS_AGENT_REPORTING_QUICK_STARTS: readonly string[] = [
  "Which locations have the lowest course completion?",
  "Who is overdue on assigned training?",
];
export const OPUS_AGENT_BUILDING_QUICK_STARTS: readonly string[] = [
  "Draft a check-in for new hires after their first week.",
  "Turn our closing procedures into a checklist.",
];
export const OPUS_AGENT_EXAMPLE_PROMPTS: readonly string[] = [
  ...OPUS_AGENT_REPORTING_QUICK_STARTS,
  ...OPUS_AGENT_BUILDING_QUICK_STARTS,
];

// Layout duplicated from the Ask Opus ChatThread / KnowledgeBaseChatSidebar
// empty state, driven by polled turns instead of streamed messages.
export const OpusAgentThread: React.FC<OpusAgentThreadProps> = ({
  turns,
  loading,
  loadFailed,
  scrollerRef,
  chatKey,
  onSendMessage,
  onAttachFiles,
  onResumePolling,
  onRetryLoad,
  onEditMessage,
  agentName,
  agentSuggestedQuestions = [],
  agentDescription,
  agentCapabilities: agentCapabilitiesList = [],
  userFirstName,
  canBuild = false,
  canSend = true,
  openerIcon,
  finishBeat,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const quickStarts = agentName
    ? agentSuggestedQuestions
    : canBuild
      ? OPUS_AGENT_EXAMPLE_PROMPTS
      : OPUS_AGENT_REPORTING_QUICK_STARTS;
  // Opens and chat switches paint already scrolled to the bottom; incoming
  // events keep the view pinned unless the reader scrolled up. A new last
  // turn (above all, a message sent from here) always scrolls into view.
  useStickToBottom({
    scrollerRef,
    contentRef,
    resetKey: chatKey,
    followKey: turns.at(-1)?.clientKey ?? turns.at(-1)?.id,
  });

  const isEmpty = turns.length === 0 && !loading && !loadFailed;
  const plans = useMemo(
    () => planProposalStates(turns, { canSend }),
    [turns, canSend],
  );
  const questions = useMemo(
    () => questionsStates(turns, { canSend }),
    [turns, canSend],
  );
  // Like Approve: the card folds once the answer sends, so focus goes to
  // the composer.
  // An empty message (every question skipped) sends nothing: the admin
  // answers in the composer instead.
  const answerQuestions = (message: string, withFile: boolean) => {
    if (message && withFile && onAttachFiles) onAttachFiles(message, true);
    else if (message) onSendMessage(message);
    focusNearestComposer(contentRef.current);
  };
  const startWith = (prompt: string) => {
    if (onAttachFiles && promptNeedsFile(prompt)) onAttachFiles(prompt, false);
    else onSendMessage(prompt);
  };
  const openPlan = useOpusAgentCanvasStore((store) => store.openPlan);
  const closeCanvas = useOpusAgentCanvasStore((store) => store.close);
  // The card folds once the approval sends, taking its focused button with
  // it: focus goes to this panel's composer instead of falling to body.
  const approvePlan = () => {
    onSendMessage(APPROVE_PLAN_MESSAGE);
    closeCanvas();
    focusNearestComposer(contentRef.current);
  };
  const viewPlan = (eventId: string) => {
    if (chatKey) openPlan(chatKey, eventId, { focus: true });
  };

  return (
    // pt-4 keeps the first element (whatever it is) off the drawer header,
    // mirroring the h-4 spacer that ends the thread.
    <div ref={contentRef} className="flex h-full min-w-0 flex-col pt-4">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className={cn("flex w-full min-w-0 max-w-full flex-col", "gap-1")}>
          {isEmpty && (
            <OpeningMessages
              icon={openerIcon}
              name={agentName || "Ask Opus"}
              userFirstName={userFirstName}
              capabilities={
                agentName
                  ? agentCapabilitiesList
                  : askOpusCapabilities(canBuild)
              }
              fallback={
                agentName
                  ? agentDescription ||
                    "I work from the instructions you set for me. What do you need?"
                  : null
              }
              quickStarts={quickStarts}
              onSendMessage={startWith}
            />
          )}
          {loadFailed && (
            <OpusAgentErrorBubble
              message="Could not load this conversation."
              onRetry={onRetryLoad}
            />
          )}
          {turns.map((turn, index) => (
            <OpusAgentTurnMessage
              key={turn.clientKey ?? turn.id}
              finishBeat={finishBeat}
              turn={turn}
              turnIndex={index}
              onRetry={onSendMessage}
              onResumePolling={onResumePolling}
              onEditMessage={onEditMessage}
              plans={plans}
              onApprovePlan={approvePlan}
              onViewPlan={viewPlan}
              questions={questions}
              onAnswerQuestions={answerQuestions}
            />
          ))}
          <div className="h-4" />
        </div>
      </div>
    </div>
  );
};

// An empty chat reads as one the agent already opened: today's time, then
// its first messages in the same bubbles as its replies. The quick-starts sit on the reader's side as
// replies they can send.
const OpeningMessages: React.FC<{
  icon?: React.ReactNode;
  name: string;
  userFirstName?: string | null;
  capabilities: readonly string[];
  fallback: string | null;
  quickStarts: readonly string[];
  onSendMessage: (message: string) => void;
}> = ({
  icon,
  name,
  userFirstName,
  capabilities,
  fallback,
  quickStarts,
  onSendMessage,
}) => {
  // Fixed at mount: the opening doesn't tick forward while the chat sits
  // open.
  const [openedAt] = React.useState(() => new Date());
  return (
    <div
      className={cn(BUBBLE_GROUP_CLASS, "w-full max-w-full min-w-0 pt-6 pb-4")}
      data-testid="opus-agent-empty-state"
    >
      {icon && <div className="mb-2 flex justify-center">{icon}</div>}
      <Text
        variant="fine-print-description"
        className="mb-1 text-center text-text-tertiary"
      >
        {format(openedAt, "EEE, MMM d h:mm a")}
      </Text>
      <div className={cn(AGENT_BUBBLE_CLASS, "max-w-17/20")} data-bubble>
        <Text variant="compact-description" multiline>
          {userFirstName ? `Hey ${userFirstName}. ` : "Hey. "}I&apos;m {name}.
        </Text>
      </div>
      <div
        className={cn(AGENT_BUBBLE_CLASS, "max-w-17/20")}
        data-testid="opus-agent-capabilities"
        data-bubble
      >
        {capabilities.length > 0 ? (
          <>
            <Text variant="compact-description" multiline>
              Here&apos;s what I can help with:
            </Text>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {capabilities.map((item) => (
                <li key={item}>
                  <Text variant="compact-description" multiline>
                    {item}
                  </Text>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Text variant="compact-description" multiline>
            {fallback}
          </Text>
        )}
      </div>
      {quickStarts.length > 0 && (
        <div
          className="mt-3 flex w-full min-w-0 flex-col items-end gap-1.5"
          data-testid="opus-agent-example-prompts"
        >
          {quickStarts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              data-testid="opus-agent-example-prompt"
              onClick={() => onSendMessage(prompt)}
              className={cn(
                "max-w-17/20 rounded-2xl border border-border-primary bg-bg-primary px-3 py-2 text-left transition-colors hover:bg-state-hover active:bg-state-pressed",
                OPUS_AGENT_FOCUS_RING_CLASSNAME,
              )}
            >
              <Text
                variant="compact-description"
                className="min-w-0 text-text-primary"
                multiline
              >
                {prompt}
              </Text>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
